import AssignmentIcon from "@mui/icons-material/Assignment";
import CableIcon from "@mui/icons-material/Cable";
import CakeIcon from "@mui/icons-material/Cake";
import CastForEducationIcon from "@mui/icons-material/CastForEducation";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Tooltip } from "@mui/material";
import type React from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { ICON_SIZE } from "../../theme/tokens";
import { EmploymentType, NurseTrainingStatus, type TeamNurse } from "../../utils/models";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { NurseUnionIndicator } from "../NurseUnionIndicator/NurseUnionIndicator";
import { ThemedSvg } from "../UIToolkit/ThemedSvg";
import { integratorLabel } from "./teamFilterOptions";

const GLYPH_PX = Number.parseInt(ICON_SIZE.MICRO, 10);
const GLYPH_SX = { width: GLYPH_PX, height: GLYPH_PX } as const;

type EngagementBadge = { icon: React.ReactElement; color: string; tooltip: string };

/**
 * DEMO ONLY — fabricated engagement signals keyed off `nurseId % 10`.
 *
 * None of this is real data; it exists so a demo department shows a populated-looking roster. It is
 * kept here, quarantined behind the `isDemo` flag, rather than mixed in with the glyphs that mean
 * something. If demo no longer needs it, this function and its four translation keys are the whole
 * deletion.
 */
function getDemoEngagementBadge(nurseId: number, t: (key: string) => string): EngagementBadge | null {
	switch (nurseId % 10) {
		case 1:
			return { icon: <WarningAmberIcon sx={{ fontSize: GLYPH_PX }} />, color: "#d32f2f", tooltip: t("team.demo.turnover_risk_high") };
		case 2:
			return { icon: <PersonAddIcon sx={{ fontSize: GLYPH_PX }} />, color: "#2e7d32", tooltip: t("team.demo.new_hire_texts_pending") };
		case 3:
			return { icon: <CakeIcon sx={{ fontSize: GLYPH_PX }} />, color: "#7b1fa2", tooltip: t("team.demo.upcoming_birthday") };
		case 4:
			return { icon: <AssignmentIcon sx={{ fontSize: GLYPH_PX }} />, color: "#0288d1", tooltip: t("team.demo.survey_needs_review") };
		default:
			return null;
	}
}

/**
 * The strip of small glyphs after a staff member's name: employment type, training status, charge
 * eligibility, extra shifts, union membership, and (for support users) which HRIS feeds them.
 *
 * Extracted from the Name cell, which had grown to a ten-way inline conditional mixing `.jpg`,
 * `.png`, inline SVG and MUI icons. Same glyphs, one place to read them.
 */
export const NurseNameBadges: FC<{ nurse: TeamNurse; isDemo: boolean; isSupport: boolean }> = ({ nurse, isDemo, isSupport }) => {
	const { t } = useTranslation();
	const demoBadge = isDemo ? getDemoEngagementBadge(nurse.id, t) : null;
	// Display names, not raw values — a bare "demo" reads as fake. `isDemo` means only demo
	// departments get relabelled; see integratorLabel.
	const externalSources =
		isSupport && nurse.externalEmployees?.length ? Array.from(new Set(nurse.externalEmployees.map((e) => integratorLabel(e.source, t, isDemo)))) : [];

	return (
		<Box sx={{ position: "relative", m: 0, display: "flex", gap: "4px" }}>
			{demoBadge && (
				<Tooltip title={demoBadge.tooltip}>
					<Box sx={{ color: demoBadge.color, display: "flex", alignItems: "center" }}>{demoBadge.icon}</Box>
				</Tooltip>
			)}
			{nurse.employmentType === EmploymentType.FloatPool && (
				<InHouseTooltip title={t(`tooltip.${EmploymentType.FloatPool}`)}>
					<img alt="" src="/Float_Pool.jpg" style={{ width: GLYPH_PX, height: GLYPH_PX }} />
				</InHouseTooltip>
			)}
			{nurse.employmentType === EmploymentType.PerDiem && (
				<InHouseTooltip title={t(`tooltip.${EmploymentType.PerDiem}`)}>
					<img alt="" src="/Per_Diem.jpg" style={{ width: GLYPH_PX, height: GLYPH_PX }} />
				</InHouseTooltip>
			)}
			{nurse.employmentType === EmploymentType.Travel && (
				<InHouseTooltip title={t(`tooltip.${EmploymentType.Travel}`)}>
					<img alt="" src="/Travel_Nurse.jpg" style={{ width: GLYPH_PX, height: GLYPH_PX }} />
				</InHouseTooltip>
			)}
			{nurse.trainingStatus === NurseTrainingStatus.Trainee && (
				<InHouseTooltip title={t("tooltip.nurse.trainee")}>
					<ThemedSvg src="/student.svg" size={GLYPH_PX} color="var(--inhouse-primary)" />
				</InHouseTooltip>
			)}
			{nurse.trainingStatus === NurseTrainingStatus.Trainer && (
				<InHouseTooltip title={t("tooltip.nurse.trainer")}>
					<img alt="" src="/teacher.png" style={{ width: GLYPH_PX, height: GLYPH_PX }} />
				</InHouseTooltip>
			)}
			{nurse.trainingStatus === NurseTrainingStatus.Education && (
				<InHouseTooltip title={t("tooltip.nurse.education")}>
					<CastForEducationIcon aria-label={t("education.icon")} sx={{ ...GLYPH_SX, color: "primary.dark" }} />
				</InHouseTooltip>
			)}
			{(nurse.isShiftManager || nurse.canBeReliefCharge) && (
				<InHouseTooltip title={t("tooltip.nurse.shiftManager")}>
					<ThemedSvg src="/charge.svg" size={GLYPH_PX} color="var(--inhouse-primary)" />
				</InHouseTooltip>
			)}
			{nurse?.extraShifts && (
				<InHouseTooltip title={t("tooltip.extraShifts")}>
					<ThemedSvg src="/plusIcon.svg" size={GLYPH_PX} color="var(--inhouse-primary)" />
				</InHouseTooltip>
			)}
			<NurseUnionIndicator nurseIdOrNurseObject={nurse} />
			{externalSources.length > 0 && (
				<InHouseTooltip title={t("tooltip.nurse.integrations", { sources: externalSources.join(", ") })}>
					<CableIcon sx={{ ...GLYPH_SX, color: "primary.dark" }} />
				</InHouseTooltip>
			)}
		</Box>
	);
};
